/**
 * Verwaltet den State und die Logik für Inline-Editing in Tabellen
 */
import { useState, useCallback } from 'react';
import {
  EditableTableConfig,
  InlineEditingState,
  InlineEditingActions,
} from './types';
import { ApiError } from '../../../services/api/client';

/** Temporäre ID für neue Zeilen */
export const NEW_ROW_ID = '__new__';

/** Extrahiert eine lesbare Fehlermeldung aus verschiedenen Error-Typen */
function extractErrorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.detail;
  if (error instanceof Error) return error.message;
  return 'Unbekannter Fehler';
}

export function useInlineEditing<T extends { id: string }>(
  config: EditableTableConfig<T>,
  data: T[],
  onDataChanged?: () => void
): InlineEditingState<T> & InlineEditingActions<T> {
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [newRowData, setNewRowData] = useState<Partial<T> | null>(null);
  const [pendingChanges, setPendingChanges] = useState<Record<string, Partial<T>>>({});
  const [errors, setErrors] = useState<Record<string, Record<string, string>>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const startEditing = useCallback((rowId: string) => {
    // Falls bereits eine andere Zeile bearbeitet wird, diese zuerst abschließen
    if (editingRowId && editingRowId !== rowId) {
      // Vorherige Änderungen verwerfen (oder hier könnte man auch speichern)
      setPendingChanges(prev => {
        const { [editingRowId]: _, ...rest } = prev;
        return rest;
      });
    }
    setEditingRowId(rowId);
    setIsCreating(false);
  }, [editingRowId]);

  const startCreating = useCallback((initialValues?: Partial<T>) => {
    setEditingRowId(null);
    setPendingChanges({});

    const defaultValues: Partial<T> = {};

    // Default-Werte aus Dropdown-Optionen setzen
    if (config.fields) {
      Object.entries(config.fields).forEach(([key, fieldConfig]) => {
        if (fieldConfig?.type === 'dropdown' && fieldConfig.options?.length) {
          (defaultValues as any)[key] = fieldConfig.options[0].value;
        }
      });
    }

    // Übergebene initialValues überschreiben die Defaults
    setNewRowData({ ...defaultValues, ...initialValues });
    setIsCreating(true);
    setEditingRowId(NEW_ROW_ID);
  }, [config.fields]);

  const updateField = useCallback((rowId: string, field: keyof T, value: any) => {
    if (rowId === NEW_ROW_ID) {
      setNewRowData(prev => ({
        ...(prev || {}),
        [field]: value,
      } as Partial<T>));
    } else {
      setPendingChanges(prev => ({
        ...prev,
        [rowId]: {
          ...prev[rowId],
          [field]: value,
        },
      }));
    }

    setErrors(prev => {
      const rowErrors = prev[rowId];
      if (rowErrors && rowErrors[field as string]) {
        const { [field as string]: _, ...restErrors } = rowErrors;
        return {
          ...prev,
          [rowId]: restErrors,
        };
      }
      return prev;
    });
  }, []);

  const validateRow = useCallback((rowId: string, data: Partial<T>): Record<string, string> => {
    const fieldErrors: Record<string, string> = {};

    if (config.fields) {
      Object.entries(config.fields).forEach(([key, fieldConfig]) => {
        if (fieldConfig?.required) {
          const value = data[key as keyof T];
          if (value === undefined || value === null || value === '') {
            fieldErrors[key] = 'Pflichtfeld';
          } else if (fieldConfig.type === 'number') {
            const numValue = typeof value === 'string' ? parseFloat(value) : value;
            if (isNaN(numValue as number)) {
              fieldErrors[key] = 'Ungültige Zahl';
            } else if (fieldConfig.min !== undefined && (numValue as number) < fieldConfig.min) {
              fieldErrors[key] = `Mindestens ${fieldConfig.min}`;
            }
          }
        }
      });
    }

    if (config.validateRow) {
      const customErrors = config.validateRow(data);
      Object.assign(fieldErrors, customErrors);
    }

    return fieldErrors;
  }, [config]);

  /**
   * @param fieldOverride - Optionales Objekt mit Feldwerten, die den State überschreiben
   *                        (wird verwendet, um Race-Conditions bei Enter zu vermeiden)
   */
  const saveRow = useCallback(async (rowId: string, fieldOverride?: Partial<T>) => {
    if (!config.onUpdate || rowId === NEW_ROW_ID) return;

    // Merge pendingChanges mit Override (Override hat Priorität)
    const changes = fieldOverride
      ? { ...pendingChanges[rowId], ...fieldOverride }
      : pendingChanges[rowId];

    if (!changes || Object.keys(changes).length === 0) return;

    // State auch aktualisieren für Konsistenz
    if (fieldOverride) {
      setPendingChanges(prev => ({
        ...prev,
        [rowId]: { ...prev[rowId], ...fieldOverride },
      }));
    }

    // Original-Zeile finden und mit Changes zusammenführen für Validierung
    const originalRow = data.find(row => row.id === rowId);
    const mergedData: Partial<T> = { ...originalRow, ...changes };

    const rowErrors = validateRow(rowId, mergedData);
    if (Object.keys(rowErrors).length > 0) {
      setErrors(prev => ({ ...prev, [rowId]: rowErrors }));
      return;
    }

    setIsSaving(true);
    try {
      await config.onUpdate(rowId, changes);

      setPendingChanges(prev => {
        const { [rowId]: _, ...rest } = prev;
        return rest;
      });

      onDataChanged?.();
    } catch (error: unknown) {
      setErrors(prev => ({
        ...prev,
        [rowId]: { _general: extractErrorMessage(error) },
      }));
    } finally {
      setIsSaving(false);
    }
  }, [config, data, pendingChanges, validateRow, onDataChanged]);

  /**
   *                        (wird verwendet, um Race-Conditions bei Enter zu vermeiden)
   */
  const saveNewRow = useCallback(async (fieldOverride?: Partial<T>): Promise<T | null> => {
    if (!config.onCreate || !newRowData) return null;

    // Merge State mit Override (Override hat Priorität)
    const dataToSave: Partial<T> = fieldOverride
      ? { ...newRowData, ...fieldOverride }
      : newRowData;

    // State auch aktualisieren für Konsistenz
    if (fieldOverride) {
      setNewRowData(dataToSave);
    }

    const rowErrors = validateRow(NEW_ROW_ID, dataToSave);
    if (Object.keys(rowErrors).length > 0) {
      setErrors(prev => ({ ...prev, [NEW_ROW_ID]: rowErrors }));
      return null;
    }

    setIsSaving(true);
    try {
      const created = await config.onCreate(dataToSave);

      setNewRowData(null);
      setIsCreating(false);
      setEditingRowId(null);
      setErrors(prev => {
        const { [NEW_ROW_ID]: _, ...rest } = prev;
        return rest;
      });

      onDataChanged?.();

      return created;
    } catch (error: unknown) {
      setErrors(prev => ({
        ...prev,
        [NEW_ROW_ID]: { _general: extractErrorMessage(error) },
      }));
      return null;
    } finally {
      setIsSaving(false);
    }
  }, [config, newRowData, validateRow, onDataChanged]);

  const cancelEditing = useCallback(() => {
    if (editingRowId) {
      setPendingChanges(prev => {
        const { [editingRowId]: _, ...rest } = prev;
        return rest;
      });
      setErrors(prev => {
        const { [editingRowId]: _, ...rest } = prev;
        return rest;
      });
    }

    setEditingRowId(null);
    setNewRowData(null);
    setIsCreating(false);
  }, [editingRowId]);

  const setRowErrors = useCallback((rowId: string, rowErrors: Record<string, string>) => {
    setErrors(prev => ({
      ...prev,
      [rowId]: rowErrors,
    }));
  }, []);

  const deleteRow = useCallback(async (rowId: string) => {
    if (!config.onDelete) return;

    setIsSaving(true);
    try {
      await config.onDelete(rowId);

      // Falls diese Zeile bearbeitet wurde, Editing beenden
      if (editingRowId === rowId) {
        setEditingRowId(null);
      }

      setPendingChanges(prev => {
        const { [rowId]: _, ...rest } = prev;
        return rest;
      });
      setErrors(prev => {
        const { [rowId]: _, ...rest } = prev;
        return rest;
      });

      onDataChanged?.();
    } catch (error: unknown) {
      setErrors(prev => ({
        ...prev,
        [rowId]: { _general: extractErrorMessage(error) },
      }));
    } finally {
      setIsSaving(false);
    }
  }, [config, editingRowId, onDataChanged]);

  return {
    editingRowId,
    newRowData,
    pendingChanges,
    errors,
    isSaving,
    isCreating,
    startEditing,
    startCreating,
    updateField,
    saveRow,
    saveNewRow,
    cancelEditing,
    setRowErrors,
    deleteRow,
  };
}
